import * as React from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import {
  HOST_PAYMENTS_PAYOUT_TERMS_PATH,
  HOST_PAYMENTS_PAYOUT_TERMS_VERSION,
  Institution,
  InstitutionRow,
  StripeConnectCreateAccountLinkRequest,
  UserView,
  UserViewRow,
} from 'cb-model';
import { CompoundResponse } from 'gossamer-unified';
import { fetchApi, makeResetCache, notify, notifyResponse } from 'gossamer-front-end';

interface InstitutionStripeConnectPanelProps {
  institutionRow?: InstitutionRow | undefined;
}

export const InstitutionStripeConnectPanel = (props: InstitutionStripeConnectPanelProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const { institutionRow } = props;
  const hasAccount = !!institutionRow?.stripe_connect_account_id;
  const handleRefresh = () => {
    resetCache(Institution.id, String(institutionRow.id));
  };
  if (!institutionRow) return;
  return (
    <StripeConnectPanel
      handleRefresh={handleRefresh}
      hasAccount={hasAccount}
      chargesEnabled={!!institutionRow?.stripe_connect_charges_enabled}
      payoutsEnabled={!!institutionRow?.stripe_connect_payouts_enabled}
      detailsSubmitted={!!institutionRow?.stripe_connect_details_submitted}
    />
  );
};

interface UserStripeConnectPanelProps {
  userRow: UserViewRow | undefined;
}

export const UserStripeConnectPanel = (props: UserStripeConnectPanelProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const { userRow } = props;
  const hasAccount = !!userRow?.stripe_connect_account_id;
  const handleRefresh = () => {
    if (!userRow) return;
    resetCache(UserView.id, String(userRow.id));
  };
  if (!userRow) return;
  return (
    <StripeConnectPanel
      handleRefresh={handleRefresh}
      hasAccount={hasAccount}
      chargesEnabled={!!userRow?.stripe_connect_charges_enabled}
      payoutsEnabled={!!userRow?.stripe_connect_payouts_enabled}
      detailsSubmitted={!!userRow?.stripe_connect_details_submitted}
      requireHostPaymentsTermsAcceptance
      currentHostPaymentsTermsAcceptedVersion={userRow.host_payments_payouts_terms_accepted_version}
    />
  );
};

interface StripeConnectPanelProps {
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  handleRefresh: () => void;
  hasAccount: boolean;
  requireHostPaymentsTermsAcceptance?: boolean;
  currentHostPaymentsTermsAcceptedVersion?: number | null;
}

export const StripeConnectPanel = (props: StripeConnectPanelProps): React.JSX.Element => {
  const alreadyAcceptedCurrentHostTerms =
    props.requireHostPaymentsTermsAcceptance === true &&
    (props.currentHostPaymentsTermsAcceptedVersion ?? 0) >= HOST_PAYMENTS_PAYOUT_TERMS_VERSION;
  const [submitting, setSubmitting] = React.useState(false);
  const [hostPaymentsTermsAccepted, setHostPaymentsTermsAccepted] = React.useState(alreadyAcceptedCurrentHostTerms);

  React.useEffect(() => {
    if (alreadyAcceptedCurrentHostTerms) {
      setHostPaymentsTermsAccepted(true);
    }
  }, [alreadyAcceptedCurrentHostTerms]);

  const handleRefresh = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const resp: CompoundResponse = await fetchApi('POST', '/main/stripe-connect/refresh');
      notifyResponse(resp);
      props.handleRefresh();
    } catch (e) {
      console.error(e);
      notify([e?.message || 'Could not refresh Stripe account status', 'danger']);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenOnboarding = async () => {
    if (submitting || (props.requireHostPaymentsTermsAcceptance === true && !hostPaymentsTermsAccepted)) return;
    setSubmitting(true);
    try {
      const body: StripeConnectCreateAccountLinkRequest | undefined = props.requireHostPaymentsTermsAcceptance
        ? { hostPaymentsPayoutsTermsAcceptedVersion: HOST_PAYMENTS_PAYOUT_TERMS_VERSION }
        : undefined;
      const resp: CompoundResponse = await fetchApi('POST', '/main/stripe-connect/create-account-link', body);
      notifyResponse(resp);
      props.handleRefresh();

      const url = typeof resp.url === 'string' ? resp.url : null;
      if (!url) {
        notify(['Stripe onboarding link was not returned', 'danger']);
        return;
      }
      window.location.href = url;
    } catch (e) {
      console.error(e);
      notify([e?.message || 'Could not open Stripe onboarding', 'danger']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div>
        <div>
          <strong>Details submitted</strong>: {props.detailsSubmitted ? 'Yes' : 'No'}
        </div>
        <div>
          <strong>Charges enabled</strong>: {props.chargesEnabled ? 'Yes' : 'No'}
        </div>
        <div>
          <strong>Payouts enabled</strong>: {props.payoutsEnabled ? 'Yes' : 'No'}
        </div>
      </div>

      <div className="mt-3" />

      {props.requireHostPaymentsTermsAcceptance && !props.payoutsEnabled && (
        <Form.Group className="mb-3">
          <Form.Check
            id="host-payments-payout-terms"
            type="checkbox"
            checked={hostPaymentsTermsAccepted}
            onChange={(e) => setHostPaymentsTermsAccepted(e.currentTarget.checked)}
            label={
              <span>
                I agree to the{' '}
                <a href={HOST_PAYMENTS_PAYOUT_TERMS_PATH} target="_blank" rel="noreferrer">
                  Host Payments and Payout Terms
                </a>
                .
              </span>
            }
          />
          <Form.Text className="text-muted">
            Accepting these terms is required before Stripe onboarding can be started for host payouts.
          </Form.Text>
        </Form.Group>
      )}

      {!props.payoutsEnabled && (
        <>
          <Button
            size="sm"
            variant={props.hasAccount ? 'outline-primary' : 'primary'}
            onClick={handleOpenOnboarding}
            disabled={submitting || (props.requireHostPaymentsTermsAcceptance === true && !hostPaymentsTermsAccepted)}
          >
            {submitting ? 'Opening Stripe…' : props.hasAccount ? 'Continue Stripe onboarding' : 'Connect Stripe'}
          </Button>
          <span className="ms-2" />
          <Button size="sm" variant="outline-secondary" onClick={handleRefresh} disabled={submitting}>
            Refresh status
          </Button>
        </>
      )}
      {props.hasAccount && !props.payoutsEnabled && (
        <div className="mt-2">
          It can take some time for Stripe to enable payouts after onboarding is completed. If your details are correct
          and you have submitted them, please allow some time and refresh this page again later to see if payouts have
          been enabled. If there is an issue with your Stripe account, you should receive an email from Stripe with
          further information.
        </div>
      )}
    </div>
  );
};
